/**
 * OpenResourceCardTask - таск для відкриття карточок ресурсів
 *
 * Нова проста логіка:
 * - Відкриття карточок у drawer'ах з автоматичним zIndex
 * - Вкладені drawer'и (карточка над карточкою)
 * - Управління стеком відкритих карточок
 * - Інтеграція з простим HistoryStackManager
 *
 * ЛОГІКА РОБОТИ:
 * 1. Page режим: обнуляє стек історії, встановлює новий базовий URL, переходить на сторінку
 * 2. Drawer/Modal режим: додає URL в стек ТА змінює адресну строку браузера через history.pushState()
 * 3. Закриття карточок: використовує replaceState для зміни URL БЕЗ перезавантаження
 *
 * displayMode визначається за пріоритетом:
 * params.displayMode > resource.detailConfig.mode > 'drawer'
 */

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import { historyStackManager } from '@/Core/Managers/HistoryStackManager'
import { navigationService } from '@/Core/Services/NavigationService'
import { generateRegistryKey } from '@/Core/Utils/generateRegistryKey'
import { GetResourceTask } from './GetResourceTask'

// Імпортуємо функцію для маркування програмної навігації
let navigationContextInstance: any = null

// Функція для ініціалізації контексту навігації
export const setNavigationContextForTasks = (context: any) => {
    navigationContextInstance = context
}

/**
 * Режим відображення карточки ресурсу
 */
export type TResourceDetailMode = 'view' | 'edit' | 'create'

/**
 * Параметри для відкриття карточки ресурсу
 */
export interface IOpenResourceCardParams {
    /** Назва або конфігурація ресурсу */
    resource: string | IResourceConfig

    /** Значення первинного ключа (для view/edit режимів) */
    primaryFieldValue?: string | number

    /** Режим відкриття карточки */
    mode?: TResourceDetailMode

    /** Режим відображення (page/drawer/modal). Якщо не вказано - береться з конфігурації ресурсу */
    displayMode?: 'page' | 'drawer' | 'modal'

    /** Контекст списку для доступу з карточки (для перезавантаження списку після збереження) */
    listCrudContext?: any

    /** Callback при закритті карточки */
    onClose?: () => void

    /** Callback при збереженні */
    onSave?: (data: any) => Promise<void>

    /** Callback при зміні режиму */
    onModeChange?: (mode: TResourceDetailMode) => void

    /** Початкові значення для форми (для create режиму) */
    initialValues?: Record<string, any>

    /** Додаткові пропси для ResourceDetail */
    props?: Record<string, any>

    /** Налаштування drawer */
    drawer?: {
        title?: string
        width?: number
        zIndex?: number
        [key: string]: any
    }

    /** Чи заміщувати поточну карточку замість відкриття нової */
    replace?: boolean

    /** Чи відкривати в новому стеку (очистити попередні) */
    clearStack?: boolean

    /** Чи примусово встановити displayMode (ігнорувати авто-визначення) */
    forceDisplayMode?: boolean
}

/**
 * Результат відкриття карточки
 */
export interface IOpenResourceCardResult {
    /** ID відкритої карточки в стеку */
    cardId: string

    /** URL карточки (або null якщо pageUrl не вказаний) */
    url: string | null

    /** Рівень в стеку */
    level: number

    /** zIndex для drawer */
    zIndex: number

    /** Функція для закриття карточки */
    close: () => void
}

/**
 * Конфігурація активної карточки
 */
export interface IActiveResourceCard {
    id: string
    resource: string | IResourceConfig
    resourceId?: string | number | undefined
    mode: TResourceDetailMode
    displayMode: 'page' | 'drawer' | 'modal'
    url: string | null
    level: number
    zIndex: number
    timestamp: number
    listCrudContext?: any | undefined
    onClose?: (() => void) | undefined
    onSave?: ((data: any) => Promise<void>) | undefined
    onModeChange?: ((mode: TResourceDetailMode) => void) | undefined
    props?: Record<string, any> | undefined
    drawer?: Record<string, any> | undefined
}

/**
 * Клас для управління відкритими карточками ресурсів
 */
class ResourceCardManager {
    private static instance: ResourceCardManager
    private activeCards: Map<string, IActiveResourceCard> = new Map()
    private listeners: Set<(cards: IActiveResourceCard[]) => void> = new Set()
    private baseZIndex = 1000

    private constructor() {
        // Підписуємося на зміни в стеку історії
        historyStackManager.subscribe((_stack) => {
            // В простому варіанті просто сповіщуємо слухачів
            this.notifyListeners()
        })

        // Слухаємо події навігації назад/вперед браузера
        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', (event) => {
                this.handlePopState(event)
            })
        }
    }

    public static getInstance(): ResourceCardManager {
        if (!ResourceCardManager.instance) {
            ResourceCardManager.instance = new ResourceCardManager()
        }
        return ResourceCardManager.instance
    }

    /**
     * Відкриває карточку ресурсу
     */
    public openCard(params: IOpenResourceCardParams): IOpenResourceCardResult {
        const {
            resource,
            primaryFieldValue,
            mode = 'view',
            displayMode: paramDisplayMode,
            listCrudContext,
            onClose,
            onSave,
            onModeChange,
            initialValues,
            props = {},
            drawer = {},
            replace = false,
            clearStack = false,
            forceDisplayMode = false,
        } = params

        // Отримуємо назву ресурсу
        const resourceName = typeof resource === 'string' ? resource : resource.key || 'unknown'

        // Отримуємо конфігурацію ресурсу
        // Якщо передано рядок - використовуємо GetResourceTask для пошуку в реєстрі
        const resourceConfig = typeof resource === 'string' ? GetResourceTask(resource) : resource

        // Визначаємо displayMode на основі пріоритетів:
        // 1. Параметр displayMode (найвищий пріоритет)
        // 2. detailConfig.modeConfig[mode].displayMode з конфігурації ресурсу (для конкретного режиму)
        // 3. detailConfig.mode з конфігурації ресурсу (загальний для всіх режимів)
        // 4. За замовчуванням 'drawer'
        const displayMode = paramDisplayMode || resourceConfig?.detailConfig?.modeConfig?.[mode]?.displayMode || resourceConfig?.detailConfig?.mode || 'drawer'

        // Будуємо URL для карточки
        // Передаємо об'єкт ресурсу якщо він є, щоб можна було використати pageUrl напряму
        // Повертає null якщо pageUrl не вказаний
        const url = historyStackManager.buildResourceUrl(resource, mode, primaryFieldValue)

        // Маркуємо навігацію як програмну для drawer/modal режимів
        // Це важливо для коректного визначення displayMode в ResourceDetail
        if ((displayMode === 'drawer' || displayMode === 'modal') && navigationContextInstance) {
            navigationContextInstance.markProgrammaticNavigation()
        }

        // Якщо режим 'page' - обнуляємо стек і робимо навігацію
        if (displayMode === 'page') {
            // Якщо URL null (немає pageUrl) - не можемо відкрити в page режимі
            if (!url) {
                console.error(
                    `[OpenResourceCardTask] Неможливо відкрити ресурс "${resourceName}" в page режимі: pageUrl не вказаний`
                )
                // Повертаємо dummy результат
                return {
                    cardId: 'error-no-url',
                    url: '',
                    level: 0,
                    zIndex: 0,
                    close: () => {},
                }
            }

            // Встановлюємо новий базовий URL в історії
            historyStackManager.setPageCard(url)

            if (navigationService.isReady()) {
                // Використовуємо React Router навігацію (SPA режим, без перезавантаження)
                navigationService.navigateTo(url)
            } else {
                // Fallback - звичайна навігація через window.location (повне перезавантаження сторінки)
                console.warn(
                    'NavigationService не готовий, використовується fallback навігація через window.location.href'
                )
                if (typeof window !== 'undefined') {
                    window.location.href = url
                }
            }

            // Повертаємо результат для сумісності API
            return {
                cardId: 'page-navigation',
                url,
                level: 0,
                zIndex: 0,
                close: () => {
                    // Для page режиму close не має сенсу
                    console.warn('Cannot close page mode navigation')
                },
            }
        }

        // Далі логіка для drawer/modal режимів
        // Очищуємо стек якщо потрібно
        if (clearStack) {
            this.closeAllCards()
            historyStackManager.clearToBase()
        }

        // Поточний рівень в стеку
        const currentLevel = historyStackManager.getCurrentLevel()
        const newLevel = replace ? currentLevel : currentLevel + 1

        // Генеруємо унікальний ID карточки
        const cardId = this.generateCardId(resourceName, primaryFieldValue, mode)

        // Автоматична генерація registryKey для карточки
        const autoRegistryKey = generateRegistryKey.card(resourceName, mode, primaryFieldValue)

        // Закриваємо попередню карточку якщо заміщуємо
        if (replace && this.activeCards.size > 0) {
            const lastCard = Array.from(this.activeCards.values()).pop()
            if (lastCard) {
                this.closeCard(lastCard.id)
            }
        }

        // Обчислюємо zIndex
        const zIndex = drawer.zIndex || this.calculateNextZIndex()

        // Додаємо в стек історії для drawer/modal режимів ТІЛЬКИ якщо є URL (pageUrl вказаний)
        if ((displayMode === 'drawer' || displayMode === 'modal') && url) {
            historyStackManager.pushDrawerCard(url)

            // Змінюємо URL в браузері для drawer/modal карточок
            if (typeof window !== 'undefined') {
                window.history.pushState(
                    {
                        cardId,
                        displayMode,
                        level: newLevel,
                        resourceName,
                        primaryFieldValue: primaryFieldValue?.toString(),
                        mode,
                    },
                    '',
                    url
                )
            }
        }

        // Створюємо карточку
        const card: IActiveResourceCard = {
            id: cardId,
            resource,
            resourceId: primaryFieldValue,
            mode,
            displayMode,
            url,
            level: newLevel,
            zIndex,
            timestamp: Date.now(),
            listCrudContext,
            onClose: () => {
                // Викликаємо callback користувача
                if (onClose) onClose()
                // Закриваємо карточку
                this.closeCard(cardId)
            },
            onSave,
            onModeChange: (newMode: TResourceDetailMode) => {
                // Оновлюємо режим карточки
                this.updateCardMode(cardId, newMode)
                // Викликаємо callback користувача
                if (onModeChange) onModeChange(newMode)
            },
            props: {
                ...props,
                registryKey: autoRegistryKey, // Додаємо автоматично згенерований ключ для registry
                initialValues: mode === 'create' ? initialValues : undefined,
            },
            drawer: {
                title: drawer.title || this.getCardTitle(resource, mode, primaryFieldValue),
                width: drawer.width || 500,
                zIndex,
                ...drawer,
            },
        }

        // Зберігаємо карточку
        this.activeCards.set(cardId, card)

        // Сповіщуємо слухачів
        this.notifyListeners()

        // Повертаємо результат
        return {
            cardId,
            url,
            level: newLevel,
            zIndex,
            close: () => this.closeCard(cardId),
        }
    }

    /**
     * Закриває карточку за ID
     *
     * Логіка:
     * - Для drawer/modal: використовуємо replaceState для зміни URL БЕЗ перезавантаження
     * - Для page: не підтримується (карточки page самі управляють навігацією)
     */
    public closeCard(cardId: string): boolean {
        const card = this.activeCards.get(cardId)
        
        if (!card) return false

        // Видаляємо карточку зі стеку
        this.activeCards.delete(cardId)

        // Для drawer/modal режимів - використовуємо replaceState ТІЛЬКИ якщо є URL
        if ((card.displayMode === 'drawer' || card.displayMode === 'modal') && card.url) {
            // Отримуємо попередній URL зі стеку історії
            const previousUrl = historyStackManager.popCard()

            if (typeof window !== 'undefined' && previousUrl) {
                // Використовуємо replaceState для зміни URL БЕЗ перезавантаження
                window.history.replaceState(
                    {
                        // Зберігаємо стан попередньої карточки якщо вона є
                        displayMode: 'drawer', // Попередня карточка теж drawer
                    },
                    '',
                    previousUrl
                )
            }
        }

        // Сповіщуємо слухачів
        this.notifyListeners()

        return true
    }

    /**
     * Закриває всі карточки
     */
    public closeAllCards(): void {
        this.activeCards.clear()
        this.notifyListeners()
    }

    /**
     * Отримує активні карточки
     */
    public getActiveCards(): IActiveResourceCard[] {
        return Array.from(this.activeCards.values()).sort((a, b) => a.level - b.level)
    }

    /**
     * Отримує карточку за ID
     */
    public getCard(cardId: string): IActiveResourceCard | undefined {
        return this.activeCards.get(cardId)
    }

    /**
     * Підписка на зміни активних карточок
     */
    public subscribe(listener: (cards: IActiveResourceCard[]) => void): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    /**
     * Оновлює режим карточки
     */
    private updateCardMode(cardId: string, mode: TResourceDetailMode): void {
        const card = this.activeCards.get(cardId)
        if (!card) return

        // Оновлюємо карточку
        const updatedCard = { ...card, mode }

        // Оновлюємо URL
        const resourceName = typeof card.resource === 'string' ? card.resource : card.resource.key || 'unknown'
        updatedCard.url = historyStackManager.buildResourceUrl(resourceName, mode, card.resourceId)

        // Оновлюємо заголовок
        if (updatedCard.drawer) {
            updatedCard.drawer['title'] = this.getCardTitle(card.resource, mode, card.resourceId)
        }

        this.activeCards.set(cardId, updatedCard)
        this.notifyListeners()

        // Оновлюємо URL в браузері ТІЛЬКИ якщо URL не null (pageUrl вказаний)
        if (typeof window !== 'undefined' && updatedCard.url) {
            window.history.replaceState({ cardId, mode }, '', updatedCard.url)
        }
    }

    /**
     * Генерує унікальний ID карточки
     */
    private generateCardId(resourceName: string, resourceId?: string | number, mode?: string): string {
        const parts = [resourceName, mode || 'view']
        if (resourceId) parts.push(resourceId.toString())
        return `card-${parts.join('-')}-${Date.now()}`
    }

    /**
     * Обчислює наступний zIndex
     */
    private calculateNextZIndex(): number {
        let maxZIndex = this.baseZIndex

        // Знаходимо максимальний zIndex серед активних карточок
        for (const card of this.activeCards.values()) {
            if (card.zIndex > maxZIndex) {
                maxZIndex = card.zIndex
            }
        }

        // Також перевіряємо DOM елементи
        if (typeof document !== 'undefined') {
            const drawers = document.querySelectorAll('.ant-drawer')
            drawers.forEach((drawer) => {
                const zIndex = parseInt(window.getComputedStyle(drawer).zIndex, 10)
                if (!isNaN(zIndex) && zIndex > maxZIndex) {
                    maxZIndex = zIndex
                }
            })
        }

        return maxZIndex + 10
    }

    /**
     * Отримує заголовок карточки
     */
    private getCardTitle(
        resource: string | IResourceConfig,
        mode: TResourceDetailMode,
        resourceId?: string | number
    ): string {
        const resourceTitle = typeof resource === 'string' ? resource : resource.title || resource.key || 'Resource'

        switch (mode) {
            case 'create':
                return `Create ${resourceTitle}`
            case 'edit':
                return `Edit ${resourceTitle}${resourceId ? ` #${resourceId}` : ''}`
            case 'view':
            default:
                return `${resourceTitle}${resourceId ? ` #${resourceId}` : ''}`
        }
    }

    /**
     * Обробляє події popstate (навігація назад/вперед браузера)
     */
    private handlePopState(_event: PopStateEvent): void {
        const currentUrl = window.location.pathname

        // Отримуємо поточний стек URL'ів з менеджера історії
        const historyStack = historyStackManager.getHistoryStack()

        // Якщо поточний URL не є верхнім в стеку, то потрібно закрити відповідні карточки
        const topUrl = historyStack[historyStack.length - 1]

        if (currentUrl !== topUrl) {
            // Знаходимо карточки, які потрібно закрити
            const cardsToClose = Array.from(this.activeCards.values()).filter((card) => {
                // Пропускаємо карточки без URL (pageUrl не вказаний)
                if (!card.url) return false
                
                const cardInStack = historyStack.includes(card.url)
                const cardAfterCurrent = historyStack.indexOf(card.url) > historyStack.indexOf(currentUrl)
                return cardInStack && cardAfterCurrent
            })

            if (cardsToClose.length > 0) {
                cardsToClose.forEach((card) => {
                    // Видаляємо карточку але НЕ змінюємо URL щоб уникнути циклу
                    this.activeCards.delete(card.id)
                    historyStackManager.popCard()
                    // Викликаємо onClose callback щоб відбулося перезавантаження списку
                    if (card.onClose) {
                        card.onClose()
                    }
                })
                this.notifyListeners()
            }
        }
    }

    /**
     * Сповіщає слухачів про зміни
     */
    private notifyListeners(): void {
        const cards = this.getActiveCards()
        this.listeners.forEach((listener) => listener(cards))
    }
}

// Експортуємо singleton instance
const resourceCardManager = ResourceCardManager.getInstance()

/**
 * Таск для відкриття карточки ресурсу
 */
export const OpenResourceCardTask = (params: IOpenResourceCardParams): IOpenResourceCardResult => {
    return resourceCardManager.openCard(params)
}

/**
 * Таск для закриття карточки
 */
export const CloseResourceCardTask = (cardId: string): boolean => {
    return resourceCardManager.closeCard(cardId)
}

/**
 * Таск для закриття всіх карточок
 */
export const CloseAllResourceCardsTask = (): void => {
    return resourceCardManager.closeAllCards()
}

/**
 * Таск для отримання активних карточок
 */
export const GetActiveResourceCardsTask = (): IActiveResourceCard[] => {
    return resourceCardManager.getActiveCards()
}

/**
 * Таск для підписки на зміни карточок
 */
export const SubscribeToResourceCardsTask = (listener: (cards: IActiveResourceCard[]) => void): (() => void) => {
    return resourceCardManager.subscribe(listener)
}

// Експортуємо менеджер для прямого використання
export { resourceCardManager }

export default OpenResourceCardTask
